// rng.ts — seeded, deterministic randomness.
//
// Every effect that jitters (screenshake offsets, particle velocities) pulls from THIS rng, not
// Math.random(). That is what makes `?seed=1234&replay=demo` reproduce identical motion frame for
// frame — which is the whole point of §5 in JUICE-RIG.md: an agent that can't watch an animation
// can still capture the same moment before/after a feel.json change and compare precisely.

/** mulberry32 — tiny, fast, good-enough PRNG. Same seed => same stream. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Hash an arbitrary string seed (e.g. `?seed=demo`) into a 32-bit int. */
export function hashSeed(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** The app-wide seeded stream. `?seed=` on the URL overrides the default. */
const urlSeed = new URLSearchParams(location.search).get("seed");
export const SEED = urlSeed ? hashSeed(urlSeed) : 0x1234abcd;

/** Shared deterministic float in [0,1). */
export const rand = mulberry32(SEED);

/** Deterministic float in [min,max). */
export function range(min: number, max: number): number {
  return min + rand() * (max - min);
}

/**
 * A private deterministic stream, forked from SEED by `salt`. Use for high-frequency visual jitter
 * (e.g. screenshake's per-frame offsets) that must NOT advance the shared `rand` stream: its draw
 * count is framerate-dependent, so if it shared `rand` it would shift every downstream consumer
 * (personality flirt/emote rolls, particle velocities) and break `?seed=` reproducibility across
 * machines. Same SEED+salt => same stream. */
export function forkRand(salt: number): () => number {
  return mulberry32((SEED ^ salt) >>> 0);
}
